import {
  Button,
  CircularProgress,
  Container,
  MenuItem,
  Select,
  styled,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useDispatch } from 'react-redux';
import remarkGfm from 'remark-gfm';
import supersub from 'remark-supersub';

import { FeedbackContext } from '../../constants/feedbackConstants';
import { usePrevious } from '../../hooks/usePrevious';
import { updateFeedback } from '../../redux/actions/projectQuestionActions';
import { AppDispatch } from '../../redux/store';
import { fetchQuestionAllVersions, validateFindings } from '../../services/dealGPT';
import type {
  QuestionType,
  QuestionTypeTreeNode,
  ValidateFindingsOptions,
} from '../../Types/dealGPT';
import { DealGPTView } from '../../Types/enums';
import { FeedbackInterface } from '../../Types/feedback';
import { formatDate } from '../../util/dateUtils';
import { renderMessage } from '../../util/messageUtils';
import Feedback from '../Feedback';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import ReferenceDocuments from './PhaseTwo/ReferenceDocuments';
import StyledMarkdownTable from './PhaseTwo/StyledMarkdownTable';
import QuestionStatus from './QuestionStatus';
import { AugmentedResponseWithSources } from './SourcesDisplay';

const QuestionViewContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  gap: '29px',
  padding: '24px',
  paddingBottom: '60px',
});

const StyledButton = styled(Button)({
  width: '200px',
  height: '35px',
});

const ReactMarkdownWrapper = styled('div')({
  fontSize: '14px',
  lineHeight: '1.5rem',
  letterSpacing: '0.25px',
  'a, a:visited, a:hover, a:focus': {
    color: 'inherit',
  },
});

const TextContainer = styled('p')({
  fontSize: '14px',
  lineHeight: '1.5rem',
  letterSpacing: '0.25px',
});

const StyledSelect = styled(Select)({
  '& .MuiSelect-select': {
    padding: '10px',
    color: '#092B49',
  },
  width: '100%',
});

export type QuestionViewProps = {
  questions: QuestionTypeTreeNode[];
  question: QuestionType;
  projectId: string;
  runId: string;
  questionsTree: QuestionTypeTreeNode[];
  setQuestions: (nodes: QuestionTypeTreeNode[]) => void;
};

const QuestionView = ({
  questions,
  question,
  projectId,
  runId,
  questionsTree,
  setQuestions,
}: QuestionViewProps): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const [questionObject, setQuestionObject] = useState<QuestionType>();
  const [isLoadingValidation, setIsLoadingValidation] = useState<boolean>(false);
  const currentValidationFindingsContainerRef = useRef<null | HTMLDivElement>(null);
  const previousIsLoadingValidation = usePrevious(isLoadingValidation);
  const [allVersions, setAllVersions] = useState<QuestionTypeTreeNode[]>([]);
  const analytics = useAnalytics();

  const findandReplaceQuestion = (
    questions: QuestionTypeTreeNode[],
    questionToUpdate: QuestionType
  ) => {
    for (const question of questions) {
      if (Array.isArray(question) && question.id != questionToUpdate.id) {
        findandReplaceQuestion(question, questionToUpdate);
      } else {
        if (question.id == questionToUpdate.id) {
          question.validationApiResponse = questionToUpdate.apiResponse;
          question.validationResponse = questionToUpdate.validationResponse;
          question.validationSources = questionToUpdate.validationSources;
          question.projectQuestionFeedback = questionToUpdate.projectQuestionFeedback;
          break;
        }
      }
    }
    return questions;
  };

  const questionStatuses = useMemo(() => {
    const statuses: string[] = [];
    if (!questionObject) return statuses;
    statuses.push(questionObject.status);
    if (questionObject.validationResponse) {
      statuses.push('VALIDATED');
    }
    if (questionObject.aggregatedRelevantKeyQuestions.length > 0) {
      statuses.push('RELATED');
    }
    return statuses;
  }, [questionObject]);

  const handleFeedbackSubmit = (feedback: FeedbackInterface, projectQuestionId: string) => {
    if (questionObject) {
      dispatch(
        updateFeedback({ projectQuestionId: projectQuestionId, projectQuestionFeedback: feedback })
      );
      analytics.event(
        AnalyticsEvent.WM_IA_D_QUESTION_FEEDBACK,
        {
          id: projectQuestionId,
          value: `Feedback=${feedback.thumbsUp}`,
        },
        {
          projectId,
          runId,
          scope: questionObject.scope,
          categoryName: questionObject.category,
          questionId: projectQuestionId,
          questionStatus: questionObject.status,
          thumbsUp: feedback.thumbsUp ?? undefined,
          otherFeedback: feedback.otherFeedback ?? undefined,
          suggestedFeedback: feedback.suggestedFeedback.toString() ?? undefined,
        }
      );
      setQuestionObject((prevState) => {
        if (prevState) {
          const updatedQuestionObject = {
            ...prevState,
            projectQuestionFeedback: feedback,
          };
          const updatedQuestions = findandReplaceQuestion(questionsTree, updatedQuestionObject);
          setQuestions(updatedQuestions);
          if (allVersions && allVersions.length > 0) {
            const updatedAllVersions = findandReplaceQuestion(allVersions, updatedQuestionObject);
            setAllVersions(updatedAllVersions);
          }
          return updatedQuestionObject;
        }
      });
    }
  };

  const handleValidateFindingsClick = async () => {
    setIsLoadingValidation(true);
    try {
      const options: ValidateFindingsOptions = {
        response: questionObject?.response,
        question: questionObject?.question,
      };
      analytics.event(
        AnalyticsEvent.WM_IA_D_VALIDATE,
        {
          id: questionObject?.id,
        },
        {
          projectId,
          runId,
          scope: questionObject?.scope,
          categoryName: questionObject?.category,
          questionId: questionObject?.id,
          questionStatus: questionObject?.status,
        }
      );
      const responseValidation = questionObject
        ? await validateFindings(options, questionObject.id, projectId)
        : undefined;
      if (responseValidation && responseValidation.id) {
        setQuestionObject(responseValidation);
        const questionData = responseValidation;
        const updatedQuestions = findandReplaceQuestion(questionsTree, questionData);
        setQuestions(updatedQuestions);
        const updatedAllVersions = findandReplaceQuestion(allVersions, questionData);
        setAllVersions(updatedAllVersions);
      }
    } catch (error) {
      enqueueSnackbar('Failed to validate findings', { variant: 'error' });
    }
    setIsLoadingValidation(false);
  };

  const externalLink = ({
    href,
    children,
    ...props
  }: {
    href?: string;
    children: React.ReactNode;
  }) => {
    return (
      <a href={href || '#'} target="_blank" rel="noreferrer" {...props}>
        {children}
      </a>
    );
  };

  const handleVersionChange = useCallback(
    (value: string) => {
      const version = allVersions.find((v) => v.id === value);
      analytics.event(
        AnalyticsEvent.WM_IA_D_QUESTION_VERSION_CHANGE,
        {
          id: value,
          value: allVersions[0].category,
        },
        {
          projectId,
          runId,
          scope: questionObject?.scope,
          categoryName: questionObject?.category,
          oldVersionId: questionObject?.id,
          newVersionId: version?.id,
          oldQuestionStatus: questionObject?.status,
          newQuestionStatus: version?.status,
        }
      );
      if (version) {
        setQuestionObject(version);
      }
    },
    [allVersions, analytics, projectId, questionObject, runId]
  );

  useEffect(() => {
    if (question) {
      const loadAllVersions = async () => {
        try {
          const result = await fetchQuestionAllVersions(question.id, []);
          if (result.length > 0) {
            setAllVersions(result);
            setQuestionObject(result[0]);
          }
        } catch {
          enqueueSnackbar('Failed to get question versions. Please try again later.', {
            variant: 'error',
          });
        }
      };
      loadAllVersions();
    }
  }, [question]);

  useEffect(() => {
    if (previousIsLoadingValidation && !isLoadingValidation && questionObject?.validationResponse) {
      currentValidationFindingsContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isLoadingValidation, previousIsLoadingValidation, questionObject?.validationResponse]);

  useEffect(() => {
    if (question) {
      const questionObject = questions.find((q) => q.question === question.question);
      if (questionObject) {
        setQuestionObject(questionObject);
      }
    }
  }, [question, questions]);

  if (!questionObject) {
    return <CircularProgress sx={{ marginTop: '80px' }} />;
  }

  const validateFindingsButton = (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <StyledButton
        disabled={isLoadingValidation}
        variant="contained"
        onClick={handleValidateFindingsClick}
      >
        {questionObject.validationResponse ? 'Validate Findings Again' : 'Validate Findings'}
      </StyledButton>
      {isLoadingValidation ? <CircularProgress sx={{ marginLeft: '15px' }} /> : <></>}
    </div>
  );

  return (
    <QuestionViewContainer>
      <div>
        <Typography sx={{ fontWeight: '700', marginBottom: '8px' }}>Versions</Typography>
        <StyledSelect
          value={
            questionObject && allVersions.some((v) => v.id === questionObject.id)
              ? questionObject.id
              : ''
          }
          onChange={(event) => handleVersionChange(event.target.value as string)}
          displayEmpty
        >
          {allVersions.length > 0 ? (
            allVersions.map((version) => (
              <MenuItem key={version.id} value={version.id}>
                Generated on {version.createdAt && formatDate(version.createdAt)}
              </MenuItem>
            ))
          ) : (
            <MenuItem value="">No versions available</MenuItem>
          )}
        </StyledSelect>
      </div>
      <div style={{ display: 'flex' }}>
        <QuestionStatus statuses={questionStatuses} />
      </div>
      <div>
        <Typography sx={{ fontWeight: '700', marginBottom: '8px' }}>Response</Typography>
        <ReactMarkdownWrapper>
          <ReactMarkdown
            remarkPlugins={[remarkGfm, supersub]}
            components={{
              sup: ({ children }) => (
                <AugmentedResponseWithSources
                  text={children.toString()}
                  sources={questionObject.documents ?? []}
                />
              ),
              a: externalLink,
            }}
          >
            {renderMessage({
              message: questionObject.response || '',
              sources: questionObject.documents || [],
            })}
          </ReactMarkdown>
        </ReactMarkdownWrapper>
      </div>
      {questionObject.aggregatedRelevantKeyQuestions.length > 0 && (
        <div>
          <Typography sx={{ fontWeight: '700', marginBottom: '8px' }}>
            Related Key Questions
          </Typography>
          {questionObject.aggregatedRelevantKeyQuestions.map((keyQuestion, index) => (
            <Typography key={index} sx={{ fontWeight: '400' }}>
              {keyQuestion.question}
            </Typography>
          ))}
        </div>
      )}
      <div>
        <Typography sx={{ fontWeight: '700', marginBottom: '8px' }}>Findings</Typography>
        <ReactMarkdownWrapper>
          {validateFindingsButton}
          <StyledMarkdownTable>
            <ReactMarkdown
              remarkPlugins={[remarkGfm, supersub]}
              components={{
                sup: ({ children }) => (
                  <AugmentedResponseWithSources
                    text={children.toString()}
                    sources={questionObject.documents ?? []}
                  />
                ),
                a: externalLink,
              }}
            >
              {renderMessage({
                message: questionObject.explanation || '',
                sources: questionObject.documents || [],
              })}
            </ReactMarkdown>
          </StyledMarkdownTable>
        </ReactMarkdownWrapper>
      </div>
      <div>
        <Feedback
          initialFeedback={questionObject.projectQuestionFeedback as FeedbackInterface | undefined}
          context={FeedbackContext.PROJECT_QUESTION}
          onFeedbackSubmit={(feedback) => handleFeedbackSubmit(feedback, questionObject.id)}
        />
      </div>
      <div>
        <Typography sx={{ fontWeight: '700', marginBottom: '8px' }}>Source(s)</Typography>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {questionObject.documents?.length == 0 ? (
            <TextContainer>No sources found.</TextContainer>
          ) : (
            <ReferenceDocuments
              questionId={questionObject?.id}
              view={DealGPTView.DISCOVERY}
              showAnalysisText={false}
              documents={questionObject.documents}
            />
          )}
        </div>
      </div>
      {isLoadingValidation ? (
        <CircularProgress sx={{ margin: 'auto' }} />
      ) : (
        questionObject.validationResponse && (
          <div ref={currentValidationFindingsContainerRef}>
            <Typography sx={{ fontWeight: '700', marginBottom: '8px' }}>
              Validation Source(s)
            </Typography>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '12px' }}
            >
              {questionObject.validationSources?.length == 0 ? (
                <TextContainer>No sources found.</TextContainer>
              ) : (
                <ReferenceDocuments
                  questionId={questionObject?.id}
                  view={DealGPTView.DISCOVERY}
                  showAnalysisText={false}
                  documents={questionObject.validationSources}
                />
              )}
            </div>
            <Typography sx={{ fontWeight: '700' }}>Validation Findings</Typography>
            <ReactMarkdownWrapper>
              <ReactMarkdown
                remarkPlugins={[remarkGfm, supersub]}
                components={{
                  sup: ({ children }) => (
                    <AugmentedResponseWithSources
                      text={children.toString()}
                      sources={questionObject.validationSources ?? []}
                    />
                  ),
                  a: externalLink,
                }}
              >
                {renderMessage({
                  message: questionObject.validationResponse || '',
                  sources: questionObject.validationSources || [],
                })}
              </ReactMarkdown>
            </ReactMarkdownWrapper>
          </div>
        )
      )}
    </QuestionViewContainer>
  );
};

export default QuestionView;
